<template>
  <v-form v-model="form.valid">
    <v-alert class="mt-4" type="success" text border="left" icon="mdi-lightbulb-on-outline">
      {{ $t('card-alert') }}
      <a href="https://stripe.com/" style="text-decoration: none;" target="_blank">Stripe</a>.
      {{ $t('info-alert') }}
    </v-alert>

    <v-radio-group v-model="useDefaultPaymentMethodRadio" v-if="hasDefaultPaymentMethod">
      <v-radio :disabled="!hasDefaultPaymentMethod" :label="$t('default-payment')" :value="true">
      </v-radio>

      <small v-if="hasDefaultPaymentMethod" class="mb-2"
             style="color: rgba(0, 0, 0, 0.6); font-weight: 400; line-height: 1.375rem; letter-spacing: 0.0071428571em;">
        <span v-if="$account.current.customer.payment_method.type === 'card'"> {{ $t('cb') }}</span>
        {{ $account.current.customer.payment_method.card.brand.toUpperCase() }} :
        XXXX XXXX XXXX {{ $account.current.customer.payment_method.card.last4 }}
        ( {{ $t('expire') }} {{ $account.current.customer.payment_method.card.exp_month }} /
        {{ $account.current.customer.payment_method.card.exp_year }})
      </small>

      <v-radio :label="($t('new-payment'))" :value="false">
      </v-radio>
    </v-radio-group>

    <v-row v-show="!useDefaultPaymentMethod">
      <v-col cols="12">
        <v-text-field
            @keydown.enter.prevent="submit()"
            :label="($t('label-name'))"
            v-model="form.fields.name"
            :rules="form.rules.name"
            persistent-hint
            autofocus
            :hint="($t('hint-name'))"
            required>
        </v-text-field>
      </v-col>
      <v-col cols="12" class="pt-4">
        <v-layout v-if="!stripe.visible" justify-center align-center>
          <v-progress-circular
              indeterminate width="3"
              color="primary">
          </v-progress-circular>
        </v-layout>

        <div :class="stripeElementInputClasses">
          <div class="v-input__control">
            <div class="v-input__slot">
              <div class="v-text-field__slot">
                <div id="card-element" style="width: 100%;" class="mb-2"></div>
              </div>
            </div>
            <div class="v-text-field__details">
              <div :class="'v-messages theme--light' + (stripe.error !== null ? 'error--text' : '')">
                <div class="v-messages__wrapper">
                  <div class="v-messages__message">
                    <span v-if="stripe.error !== null">{{ stripe.error }}</span>
                    <span v-else> {{ $t('card-details-input') }} </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { get } from 'lodash'

export default {
  name: 'APaymentMethodForm',
  data () {
    return {
      useDefaultPaymentMethodRadio: true,
      stripe: {
        valid: false,
        error: null,
        focus: false,
        visible: false,
        stripe: null,
        scripts: null,
        elements: null,
        card: null,
      },
      form: {
        valid: false,
        fields: {
          name: '',
        },
        rules: {
          name: [v => !!v || this.$t('required-name')],
        }
      }
    }
  },
  computed: {
    stripeElementInputClasses () {
      let classes = 'mb-6 v-input v-input--is-label-active v-input--is-dirty theme--light v-text-field v-text-field--is-booted'
      classes += this.stripe.focus ? ' v-input--is-focused' : ''

      if (this.stripe.error) classes += ' error--text'
      else if (this.stripe.valid) classes += ' success--text'
      else classes += ' primary--text'

      return classes
    },
    hasDefaultPaymentMethod () {
      return !!get(this.$account.current, 'customer.payment_method')
    },
    useDefaultPaymentMethod () {
      return this.hasDefaultPaymentMethod && this.useDefaultPaymentMethodRadio
    }
  },
  watch: {
    'form.valid' () {
      this.checkValidAndEmit()
    },
    'stripe.valid' () {
      this.checkValidAndEmit()
    },
  },
  methods: {
    async pay (paymentIntent, paymentMethod) {
      const ifNewCardValid = this.form.valid && this.stripe.valid
      const ifDefaultPaymentValid = this.useDefaultPaymentMethod && paymentMethod

      if (ifNewCardValid || ifDefaultPaymentValid) {
        this.$emit('message', this.$t('payment.validating'))
        let params = this.useDefaultPaymentMethod ? { payment_method: paymentMethod } : this.getNewCardParams()
        const result = await this.stripe.stripe.confirmCardPayment(paymentIntent, params)
        if (result.error) {
          throw this.$t('payment.reject') + result.error.message
        } else {
          return this.$t('payment.success')
        }
      } else {
        throw this.$t('invalid')
      }
    },
    async save () {
      if (this.form.valid && this.stripe.valid) {
        // Validating credit card by creating a setup intent and confirming the card to stripe
        this.$emit('message', this.$t('payment.validating'))
        const intent = (await this.$api.get('account/customer/setup_intent')).data.intent
        const result = await this.stripe.stripe.confirmCardSetup(intent, {
          payment_method: this.getNewCardParams()['payment_method']
        })
        if (result.error) throw this.$t('payment.reject-add') + result.error.message

        // If the card if valid (did not throw error), update account and save the card
        this.$emit('message', this.$t('payment.update'))
        await this.$api.post('account/customer/payment_method', {
          payment_method: result.setupIntent.payment_method
        })

        // Then refresh the user account informations
        this.$emit('message', this.$t('informations-account'))
        await this.$account.fetch()

        return this.$t('payment.updated')
      } else {
        throw this.$t('invalid')
      }
    },

    async validate (paymentIntent, paymentMethod) {
      let params = {}
      if (this.useDefaultPaymentMethod) {
        params.payment_method = paymentMethod
      } else {
        params = this.getNewCardParams()
      }

      const result = await this.stripe.stripe.confirmCardPayment(paymentIntent, params)
      if (result.error) {
        throw this.$t('reject') + result.error.message
      } else {
        return this.$t('success')
      }
    },
    checkValidAndEmit () {
      const ifNewCardValid = this.form.valid && this.stripe.valid
      const ifDefaultPaymentValid = this.useDefaultPaymentMethod
      if (ifNewCardValid || ifDefaultPaymentValid) this.$emit('valid', true)
      else this.$emit('valid', false)
    },
    async configureStripe () {
      const Stripe = await this.$stripe.load()
      this.stripe.stripe = Stripe(process.env.VUE_APP_STRIPE_API_KEY)

      if (!this.stripe.elements) {
        this.stripe.elements = this.stripe.stripe.elements()
      }
      if (!this.stripe.card) {
        this.stripe.card = this.stripe.elements.create('card', {
          hidePostalCode: true,
          style: {
            base: {
              iconColor: '#1976D2',
              fontFamily: 'Roboto, sans-serif',
              color: 'rgba(0, 0, 0, 0.87)',
              fontSize: '16px',
              fontWeight: '400',
            },
            invalid: {
              iconColor: '#ff5252',
              color: '#ff5252',
            },
          },
        })

        this.stripe.card.mount('#card-element')

        this.stripe.card.on('ready', () => this.stripe.visible = true)
        this.stripe.card.on('focus', () => this.stripe.focus = true)
        this.stripe.card.on('blur', () => this.stripe.focus = false)
        this.stripe.card.on('change', event => {
          this.stripe.error = event.error ? event.error.message : null
          this.stripe.valid = event.complete
        })
      }
    },
    getNewCardParams () {
      return {
        setup_future_usage: 'off_session',
        payment_method: {
          card: this.stripe.card,
          billing_details: {
            name: this.form.fields.name
          }
        }
      }
    }
  },
  async mounted () {
    if (this.stripe.card) this.stripe.card.destroy()
    this.$reset()
    this.$nextTick(async () => {
      // DEV
      if (process.env.VUE_APP_DEFAULT_CUSTOMER_PAYMENT_MEHOD_OWNER_NAME) this.form.fields.name = process.env.VUE_APP_DEFAULT_CUSTOMER_PAYMENT_MEHOD_OWNER_NAME
      await this.configureStripe()
    })
  }
}
</script>

<style scoped>

</style>

<i18n>
{
	"en": {
		"card-alert": "Your card is securely linked to your account by our service provider",
		"card-details-input": "Enter your bank card number, followed by its expiration's date and the visual cryptograme.",
		"cb": "Card",
		"default-payment": "Use my default payment method",
		"expire": "expires on",
		"hint-name": "Enter the cardholder's name as indicated on your bank card.",
		"info-alert": "No sensitive information will be stored on our platform.",
		"informations-account": "Retrieving Your Account Information ...",
		"invalid": "Unable to validate the form. Some fields are invalid.",
		"label-name": "Cardholder Name",
		"new-payment": "Specify a new default payment method",
		"payment": {
			"reject": "Could not pay for the order, error:",
			"reject-add": "Could not add payment method, error:",
			"success": "Payment was successful",
			"update": "Updating your payment method ...",
			"updated": "Your payment method has been changed successfully",
			"validating": "Validation of your bank card ..."
		},
		"required-name": "The name of the cardholder is required."
	},
	"es": {
		"card-alert": "Su tarjeta bancaria está vinculada a su cuenta por nuestro proveedor de servicios bancarios.",
		"card-details-input": "Ingrese su número de tarjeta bancaria, seguido de su fecha de vencimiento y el criptograma visual.",
		"cb": "Tarjeta",
		"default-payment": "Usar mi método de pago predeterminado",
		"expire": "caduca el",
		"hint-name": "Ingrese el nombre del titular de la tarjeta como se indica en su tarjeta bancaria.",
		"info-alert": "No se almacenará información sensible en nuestra plataforma.",
		"informations-account": "Recuperando la información de su cuenta ...",
		"invalid": "No se pudo validar el formulario. Algunos campos son inválidos.",
		"label-name": "Nombre del titular",
		"new-payment": "Especifique un nuevo método de pago predeterminado",
		"payment": {
			"reject": "No se pudo pagar el pedido, error:",
			"reject-add": "No se pudo agregar el método de pago, error:",
			"success": "El pago fue exitoso",
			"update": "Actualización de su método de pago ...",
			"updated": "Su método de pago se ha cambiado correctamente.",
			"validating": "Validación de su tarjeta bancaria ..."
		},
		"required-name": "Se requiere el nombre del titular."
	},
	"fr": {
		"card-alert": "Votre carte bancaire est liée à votre compte par notre prestataire bancaire",
		"card-details-input": "Saisissez le numéro de votre carte bancaire, suivi de sa date d'expiration et du cryptograme visuel.",
		"cb": "CB",
		"default-payment": "Utiliser mon moyen de paiement par défaut",
		"expire": "expire le",
		"hint-name": "Saisissez le nom du titulaire tel qu'indiqué sur votre carte bancaire.",
		"info-alert": "Aucune information sensible ne sera stockée sur notre plateforme.",
		"informations-account": "Récupération des informations de votre compte ...",
		"invalid": "Impossible de valider le formulaire. Certains champs sont invalides.",
		"label-name": "Nom du titulaire",
		"new-payment": "Spécifier un nouveau moyen de paiement par défaut",
		"payment": {
			"reject": "Impossible de payer la commande, erreur : ",
			"reject-add": "Impossible d'ajouter la méthode de paiement, erreur : ",
			"success": "Le paiement a été réalisé avec succès",
			"update": "Mise à jour de votre méthode de paiement ...",
			"updated": "Votre méthode de paiement a été modifié avec succès",
			"validating": "Validation de votre carte bancaire ..."
		},
		"required-name": "Le nom du titulaire est requis."
	},
	"it": {
		"card-alert": "La tua carta bancaria è collegata al tuo account dal nostro fornitore bancario",
		"card-details-input": "Inserisci il numero della tua carta bancaria, seguito dalla sua data di scadenza e dal crittogramma visivo.",
		"cb": "Carta",
		"default-payment": "Usa il mio metodo di pagamento predefinito",
		"expire": "scade il",
		"hint-name": "Inserisci il nome del titolare come indicato sulla tua carta bancaria.",
		"info-alert": "Nessuna informazione sensibile verrà memorizzata sulla nostra piattaforma.",
		"informations-account": "Recupero delle informazioni del tuo account ...",
		"invalid": "Impossibile convalidare il modulo. Alcuni campi non sono validi.",
		"label-name": "Nome del titolare",
		"new-payment": "Specifica un nuovo metodo di pagamento predefinito",
		"payment": {
			"reject": "Impossibile pagare l'ordine, errore:",
			"reject-add": "Impossibile aggiungere il metodo di pagamento, errore:",
			"success": "Il pagamento è stato effettuato con successo",
			"update": "Aggiornamento del tuo metodo di pagamento ...",
			"updated": "Il tuo metodo di pagamento è stato modificato con successo",
			"validating": "Convalida della tua carta bancaria ..."
		},
		"required-name": "Il nome del titolare è obbligatorio."
	},
	"pt": {
		"card-alert": "O seu cartão bancário está ligado à sua conta pelo nosso fornecedor bancário",
		"card-details-input": "Introduza o número do seu cartão bancário, seguido da data de validade e do criptograma visual.",
		"cb": "Cartão",
		"default-payment": "Usar o meu método de pagamento predefinido",
		"expire": "expira em",
		"hint-name": "Introduza o nome do titular como indicado no seu cartão bancário.",
		"info-alert": "Nenhuma informação sensível será armazenada na nossa plataforma.",
		"informations-account": "A obter as informações da sua conta ...",
		"invalid": "Impossível validar o formulário. Alguns campos são inválidos.",
		"label-name": "Nome do titular",
		"new-payment": "Especificar um novo método de pagamento predefinido",
		"payment": {
			"reject": "Impossível pagar o pedido, erro:",
			"reject-add": "Impossível adicionar o método de pagamento, erro:",
			"success": "O pagamento foi realizado com sucesso",
			"update": "A atualizar o seu método de pagamento ...",
			"updated": "O seu método de pagamento foi alterado com sucesso",
			"validating": "Validação do seu cartão bancário ..."
		},
		"required-name": "O nome do titular é obrigatório."
	},
	"pl": {
		"card-alert": "Twoja karta bankowa jest bezpiecznie powiązana z Twoim kontem przez naszego dostawcę usług",
		"card-details-input": "Wprowadź numer swojej karty bankowej, a następnie datę ważności i kod wizualny.",
		"cb": "Karta",
		"default-payment": "Użyj mojej domyślnej metody płatności",
		"expire": "wygasa",
		"hint-name": "Wprowadź imię i nazwisko posiadacza karty tak jak wskazano na karcie bankowej.",
		"info-alert": "Na naszej platformie nie będą przechowywane żadne wrażliwe informacje.",
		"informations-account": "Pobieranie informacji o Twoim koncie ...",
		"invalid": "Nie można zwalidować formularza. Niektóre pola są nieprawidłowe.",
		"label-name": "Imię i nazwisko posiadacza",
		"new-payment": "Określ nową domyślną metodę płatności",
		"payment": {
			"reject": "Nie można opłacić zamówienia, błąd:",
			"reject-add": "Nie można dodać metody płatności, błąd:",
			"success": "Płatność zakończona pomyślnie",
			"update": "Aktualizacja Twojej metody płatności ...",
			"updated": "Twoja metoda płatności została pomyślnie zmieniona",
			"validating": "Walidacja Twojej karty bankowej ..."
		},
		"required-name": "Imię i nazwisko posiadacza jest wymagane."
	}
}
</i18n>
